import React from 'react';
import PropTypes from 'prop-types';
import oneschemaImporter from '@oneschema/importer';
import isEmpty from 'lodash.isempty';
import uniqueId from 'lodash.uniqueid';

import * as API from './api';
import { Button, Select } from '../../../../components';

import './styles.scss';

const Attribute = ({ children, label }) =>
	isEmpty(children) ? null : (
		<>
			{label}: {children} <br />
		</>
	);

Attribute.propTypes = {
	children: PropTypes.string,
	label: PropTypes.string,
};

export default class AcrsUpload extends React.Component {
	static propTypes = {
		practice: PropTypes.object,
		showNotification: PropTypes.func,
		user: PropTypes.object,
	};

	state = {
		fileChanged: false,
		uploading: false,
		token: null,
		clientId: null,
		templates: [],
		templateKey: '',
		isOneSchemaOpen: false,
		oneSchemaSuccess: false,
		firstName: '',
		lastName: '',
		dob: '',
		searchType: 'patient',
		searchResults: null,
	};

	componentDidMount() {
		API.getOneSchemaToken(this.props.user.id).then(({ data }) => {
			this.setState({
				token: data.token,
				clientId: data.iss,
			});

			this.importer = oneschemaImporter({
				/* required here */
				clientId: data.iss,
				/* required here or at launch */
				// templateKey,
				userJwt: data.token,
				/* optional */
				importConfig: { type: 'local', metadataOnly: true },
				devMode: process.env.NODE_ENV != 'production',
				className: 'oneschema-importer',
				styles: {
					position: 'fixed',
					top: '0',
					left: '0',
					width: '100vw',
					height: '100vh',
					zIndex: '1051',
				},
			});

			this.importer.on('cancel', () => {
				this.setState({ isOneSchemaOpen: false });
			});

			this.importer.on('success', (data) => {
				this.setState({
					isOneSchemaOpen: false,
					oneSchemaSuccess: true,
					oneSchemaData: data,
				});
			});

			this.importer.on('error', (/* message */) => {
				this.setState({ isOneSchemaOpen: false });
				// handle error
				this.props.showNotification({
					message: 'Problem with import(ed) file!',
					status: 'danger',
					timeout: 30000,
				});
			});
		});
		API.getOneSchemaTemplates(this.props.user.id).then(({ data }) => {
			this.setState({
				templates: data.templates,
			});
		});
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			!prevState.oneSchemaSuccess &&
			this.state.oneSchemaSuccess &&
			prevState.isOneSchemaOpen &&
			!this.state.isOneSchemaOpen
		) {
			// do upload
			this.handleCleanAcrsFileUpload();
		}
	}

	handleCleanAcrsFileUpload = async () => {
		await API.uploadCleanAcrsFile(this.props.user.id, this.state.oneSchemaData);
		this.props.showNotification({
			message: 'File uploaded!',
			status: 'success',
			timeout: 10000,
		});
		this.setState({
			oneSchemaData: null,
			oneSchemaSuccess: false,
		});
	};

	handleTemplateSelection = ({ target }) => {
		this.setState({
			templateKey: target.value,
		});
	};

	handleOpenOneSchemaImporter = async () => {
		const { templateKey } = this.state;
		this.setState({ isOneSchemaOpen: true });

		this.importer.launch({
			templateKey,
		});
	};

	handleSearchTypeChange = ({ target }) => {
		this.setState({
			searchType: target.value,
			searchResults: null,
		});
	};

	handleFirstNameChange = ({ target }) => {
		this.setState({ firstName: target.value });
	};

	handleLastNameChange = ({ target }) => {
		this.setState({ lastName: target.value });
	};

	handleDOBChange = ({ target }) => {
		// format from browser is YYYY-MM-DD
		this.setState({ dob: target.value });
	};

	handleACRSSearch = (event) => {
		event.preventDefault();
		const filterOptions = {
			record_limit: 10,
			offset: 0,
		};
		const { searchType, firstName, lastName, dob } = this.state;
		let isProviderQuery = false;
		if (searchType == 'patient') {
			filterOptions.Patient_First_Name = firstName;
			filterOptions.Patient_Last_Name = lastName;
			filterOptions.Patient_Date_of_Birth = isEmpty(dob)
				? null
				: dob.split('-').join('');
		} else {
			isProviderQuery = true;
			filterOptions.Provider_First_Name = firstName;
			filterOptions.Provider_Last_Name = lastName;
		}
		API.getACRSPatientList(this.props.user.id, isProviderQuery, filterOptions)
			.then(({ data }) => {
				this.setState({ searchResults: data });
			})
			.catch(() => {
				this.props.showNotification({
					message: 'Search failed!',
					status: 'danger',
					timeout: 30000,
				});
			});
	};

	render() {
		const {
			templates,
			templateKey,
			isOneSchemaOpen,
			firstName,
			lastName,
			searchType,
			searchResults,
			dob,
		} = this.state;

		const isSearchValid = !isEmpty(firstName) || !isEmpty(lastName);

		return (
			<React.Fragment>
				<form className="form-horizontal">
					<div className="form-group">
						<label className="col-md-4 control-label">Template</label>
						<div className="col-md-4">
							<Select
								options={[
									{ label: 'Choose...', value: '' },
									...templates.map(({ name, template_key }) => ({
										label: name,
										value: template_key,
									})),
								]}
								onChange={this.handleTemplateSelection}
								selectedValue={templateKey}
							/>
						</div>
					</div>
					<div className="form-group text-center">
						<Button
							title="Open Importer"
							onClick={this.handleOpenOneSchemaImporter}
							disabled={
								templates.length === 0 || templateKey === '' || isOneSchemaOpen
							}
							primary
						/>
					</div>
					<div>
						<p>
							CareConvene uses a HIPAA compliant third-party application to
							validate import files for data conformance. Select the file
							template option, then open the importer to load and process the
							file. If there are errors, please complete the following two
							steps:
						</p>
						<ol>
							<li>
								Fix the errors and resubmit the file. The ACRS files will
								process on Friday evening.
							</li>
							<li>
								Submit a support ticket via email to support@careconvene.com.
							</li>
						</ol>
						<p>
							* ACRS files should be submitted on a monthly basis to keep the
							patient attribution up to date and no longer than 90 days.
						</p>
					</div>
				</form>
				<hr />
				<h3 className="text-center">CareConvene ACRS Routing Table Search</h3>
				<form className="form-horizontal acrs-search-form">
					<div className="panel panel-default">
						<div className="panel-heading">
							<div className="row">
								<div className="col-md-2">
									<Select
										options={[
											{ label: 'Patient', value: 'patient' },
											{ label: 'Provider', value: 'provider' },
										]}
										onChange={this.handleSearchTypeChange}
										selectedValue={searchType}
										label="Search Type"
									/>
								</div>
								<div className="col-md-2">
									<div className="form-group">
										<label htmlFor="input-fname" className="control-label">
											First Name
										</label>
										<input
											id="input-fname"
											type="text"
											placeholder="First Name"
											className="form-control"
											onChange={this.handleFirstNameChange}
											value={firstName}
										/>
									</div>
								</div>
								<div className="col-md-2">
									<div className="form-group">
										<label htmlFor="input-lname" className="control-label">
											Last Name
										</label>
										<input
											id="input-lname"
											type="text"
											placeholder="Last Name"
											className="form-control"
											onChange={this.handleLastNameChange}
											value={lastName}
										/>
									</div>
								</div>
								{searchType === 'patient' && (
									<div className="col-md-2">
										<div className="form-group">
											<label htmlFor="input-dob" className="control-label">
												Date of Birth
											</label>
											<input
												id="input-dob"
												type="date"
												className="form-control"
												onChange={this.handleDOBChange}
												value={dob}
											/>
										</div>
									</div>
								)}
								<div className="col-md-1">
									<div className="form-group">
										<Button
											title="Search"
											disabled={!isSearchValid}
											onClick={this.handleACRSSearch}
											className="search-submit"
										/>
									</div>
								</div>
							</div>
						</div>
						<table className="table table-striped table-bordered table-hover">
							<thead>
								{searchType === 'patient' ? (
									<tr>
										<th>Patient</th>
										<th>DOB</th>
										<th>Phone</th>
										<th>Address</th>
										<th>Provider</th>
										<th>Practice</th>
										<th>CC Attributes</th>
									</tr>
								) : (
									<tr>
										<th>Provider</th>
										<th>NPI</th>
										<th>Practice</th>
										<th>OID</th>
									</tr>
								)}
							</thead>
							<tbody>
								{!searchResults && (
									<tr>
										<td colSpan="7">
											Please enter some search criteria and click Search
										</td>
									</tr>
								)}
								{searchResults && searchResults.length === 0 && (
									<tr>
										<td colSpan="7">No results found</td>
									</tr>
								)}
								{searchResults &&
									searchResults.length > 0 &&
									searchType === 'patient' &&
									searchResults.map((record) => (
										<tr key={`${uniqueId('patient-')}`}>
											<td>
												{record.patient_last_name}, {record.patient_first_name}
												{!isEmpty(record.gender) && ` ${record.gender}`}
											</td>
											<td>{record.patient_date_of_birth}</td>
											<td>
												{record.patient_phone_dash_mobile ||
													record.patient_phone_dash_home}
											</td>
											<td>
												{record.patient_address_1}{' '}
												{!isEmpty(record.patient_address_2) &&
													` ${record.patient_address_2}`}{' '}
												{record.patient_city} {record.patient_state}{' '}
												{record.patient_zip}
											</td>
											<td>
												{record.Attributed_Physician_First_Name}{' '}
												{record.Attributed_Physician_Last_Name}
											</td>
											<td>{record.Attributed_Practice_Unit_Name}</td>
											<td>
												<Attribute label="PP">{record.primary_payer}</Attribute>
												<Attribute label="PPP">
													{record.primary_payer_plan}
												</Attribute>
												<Attribute label="SP">
													{record.secondary_payer}
												</Attribute>
												<Attribute label="SPP">
													{record.secondary_payer_plan}
												</Attribute>
												<Attribute label="Emp">{record.employer}</Attribute>
												<Attribute label="Loc">{record.location}</Attribute>
												<Attribute label="Prog">{record.program_1}</Attribute>
											</td>
										</tr>
									))}
								{searchResults &&
									searchResults.length > 0 &&
									searchType !== 'patient' &&
									searchResults.map((record) => (
										<tr key={`${uniqueId('provider-')}`}>
											<td>
												{record.Provider_First_Name} {record.Provider_Last_Name}
											</td>
											<td>{record.Provider_NPI}</td>
											<td>{record.Practice_Name}</td>
											<td>{record.Practice_OID}</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>
				</form>
			</React.Fragment>
		);
	}
}
